"use client";

/**
 * Client body for the /about company page. Lives apart from `page.tsx` (which
 * stays a server component for `metadata` + JSON-LD) so it can call `useT()`
 * and follow the visitor's locale, exactly like the use-case pages. All copy
 * comes from the `about` namespace; structure (icons, layout) stays here.
 *
 * The page is deliberately calm and product-first: a large minimal hero, the
 * operational problem, the design principles, the story (with a subtle
 * timeline), the vision, the controlled-onboarding positioning, and a company
 * card (legal entity, no personal spotlight) at the very bottom. Styling
 * reuses the shared marketing kit + design-system tokens; the only motion is
 * the shared <Reveal> fade (which respects prefers-reduced-motion).
 */

import Link from "next/link";
import {
  Layers,
  UserCheck,
  Lightbulb,
  FileText,
  ShieldCheck,
  ScrollText,
  GitBranch,
  Boxes,
  Lock,
} from "lucide-react";
import { MarketingShell } from "@/components/marketing/marketing-shell";
import { PUBLIC_EMAIL } from "@/lib/contact";
import {
  Section,
  SectionHeader,
  FeatureGrid,
} from "@/components/marketing/marketing-kit";
import { Reveal } from "@/components/reveal";
import { useT } from "@/i18n";

export function AboutContent() {
  const t = useT();
  const k = (s: string) => `about.${s}`;

  const problems = [
    "fragmented",
    "disconnected",
    "ungoverned",
    "traceability",
    "handovers",
    "incidents",
  ].map((key) => ({
    title: t(k(`why.${key}.title`)),
    body: t(k(`why.${key}.body`)),
  }));

  const principles = [
    { key: "existing", icon: <Layers size={18} /> },
    { key: "humanApproval", icon: <UserCheck size={18} /> },
    { key: "explainable", icon: <Lightbulb size={18} /> },
    { key: "evidence", icon: <FileText size={18} /> },
    { key: "governance", icon: <ShieldCheck size={18} /> },
    { key: "auditability", icon: <ScrollText size={18} /> },
    { key: "gitops", icon: <GitBranch size={18} /> },
    { key: "tenant", icon: <Boxes size={18} /> },
    { key: "security", icon: <Lock size={18} /> },
  ].map((p) => ({
    icon: p.icon,
    title: t(k(`principles.${p.key}.title`)),
    body: t(k(`principles.${p.key}.body`)),
  }));

  const timeline = ["observation", "ai", "goal", "product"].map((key) => ({
    tag: t(k(`story.steps.${key}.tag`)),
    title: t(k(`story.steps.${key}.title`)),
    body: t(k(`story.steps.${key}.body`)),
  }));

  const vision = ["connect", "context", "govern", "incident", "oversight"].map(
    (key) => ({
      title: t(k(`vision.${key}.title`)),
      body: t(k(`vision.${key}.body`)),
    }),
  );

  return (
    <MarketingShell>
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden border-b border-line-subtle">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 -top-40 h-[520px] bg-[radial-gradient(ellipse_at_top,rgba(244,63,142,0.14),transparent_62%)]"
        />
        <div className="relative mx-auto max-w-4xl px-5 sm:px-6 pt-20 pb-20 md:pt-28 md:pb-28">
          <Reveal>
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-fg-muted">
              {t(k("hero.eyebrow"))}
            </div>
          </Reveal>
          <Reveal delay={60}>
            <h1 className="mt-6 text-balance text-[36px] font-semibold leading-[1.06] tracking-tight text-fg-bright sm:text-5xl md:text-6xl">
              {t(k("hero.title"))}
            </h1>
          </Reveal>
          <Reveal delay={120}>
            <p className="mt-7 max-w-2xl text-pretty text-lg leading-relaxed text-fg-dim">
              {t(k("hero.subtitle"))}
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── Why FixControl exists ─────────────────────────────────────────── */}
      {/* Section ids are deep-link targets for the About mega-menu items. */}
      <Section id="mission">
        <SectionHeader
          align="left"
          eyebrow={t(k("why.eyebrow"))}
          title={t(k("why.title"))}
          description={t(k("why.description"))}
        />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
          {problems.map((p, i) => (
            <Reveal key={p.title} delay={(i % 2) * 70}>
              <div className="flex gap-4">
                <span
                  aria-hidden
                  className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand-300"
                />
                <div>
                  <h3 className="text-[15px] font-semibold text-fg-bright leading-snug">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-[14px] text-fg-dim leading-relaxed">
                    {p.body}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* ── Design principles ─────────────────────────────────────────────── */}
      <Section id="principles" muted>
        <SectionHeader
          eyebrow={t(k("principles.eyebrow"))}
          title={t(k("principles.title"))}
          description={t(k("principles.description"))}
        />
        <FeatureGrid features={principles} />
      </Section>

      {/* ── The story ─────────────────────────────────────────────────────── */}
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] gap-12 lg:gap-16 items-start">
          <div className="max-w-xl">
            <SectionHeader
              align="left"
              eyebrow={t(k("story.eyebrow"))}
              title={t(k("story.title"))}
            />
            <Reveal delay={80}>
              <p className="mt-6 text-[15px] leading-relaxed text-fg-dim">
                {t(k("story.lead"))}
              </p>
            </Reveal>
            <Reveal delay={140}>
              <p className="mt-4 text-[15px] leading-relaxed text-fg-dim">
                {t(k("story.body"))}
              </p>
            </Reveal>
            <Reveal delay={200}>
              <p className="mt-6 border-l-2 border-line pl-4 text-[14px] italic leading-relaxed text-fg-muted">
                {t(k("story.attribution"))}
              </p>
            </Reveal>
          </div>

          {/* Subtle timeline — problem → principle → product. */}
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-fg-muted">
              {t(k("story.timelineTitle"))}
            </div>
            <ol className="mt-6 space-y-6 border-l border-line-subtle pl-6">
              {timeline.map((s, i) => (
                <Reveal key={s.tag} delay={i * 70} as="li">
                  <div className="relative">
                    <span
                      aria-hidden
                      className="absolute -left-[31px] top-1 h-2.5 w-2.5 rounded-full bg-brand-grad shadow-glow"
                    />
                    <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-fg-muted">
                      {s.tag}
                    </div>
                    <div className="mt-1 text-[15px] font-semibold text-fg-bright leading-snug">
                      {s.title}
                    </div>
                    <p className="mt-1.5 text-[13px] text-fg-dim leading-relaxed">
                      {s.body}
                    </p>
                  </div>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </Section>

      {/* ── Where FixControl is going ─────────────────────────────────────── */}
      <Section id="architecture" muted>
        <SectionHeader
          eyebrow={t(k("vision.eyebrow"))}
          title={t(k("vision.title"))}
          description={t(k("vision.description"))}
        />
        <ol className="mx-auto mt-12 max-w-3xl divide-y divide-line-subtle rounded-2xl border border-line-subtle bg-bg-raised">
          {vision.map((v, i) => (
            <Reveal key={v.title} delay={i * 50} as="li">
              <div className="flex items-start gap-4 px-5 py-5 sm:px-6">
                <span className="mt-0.5 text-[12px] font-mono text-fg-muted">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="text-[15px] font-semibold text-fg-bright leading-snug">
                    {v.title}
                  </h3>
                  <p className="mt-1.5 text-[14px] text-fg-dim leading-relaxed">
                    {v.body}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </ol>
      </Section>

      {/* ── Controlled onboarding, built to scale ─────────────────────────── */}
      {/* Positioning: staged rollout is a governance choice, and the platform
          is multi-tenant SaaS designed to expand after validation. */}
      <Section id="onboarding">
        <div className="mx-auto max-w-3xl">
          <SectionHeader
            align="left"
            eyebrow={t(k("onboarding.eyebrow"))}
            title={t(k("onboarding.title"))}
          />
          <Reveal delay={80}>
            <p className="mt-6 text-[15px] leading-relaxed text-fg-dim">
              {t(k("onboarding.para1"))}
            </p>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-4 text-[15px] leading-relaxed text-fg-dim">
              {t(k("onboarding.para2"))}
            </p>
          </Reveal>
          <Reveal delay={200}>
            <div className="mt-8 rounded-2xl border border-line-subtle bg-bg-raised p-6">
              <h3 className="text-[15px] font-semibold text-fg-bright leading-snug">
                {t(k("onboarding.pilotTitle"))}
              </h3>
              <p className="mt-2 text-[14px] text-fg-dim leading-relaxed">
                {t(k("onboarding.pilotBody"))}
              </p>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* ── Closing statement ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden border-b border-line-subtle">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(244,63,142,0.12),transparent_66%)]"
        />
        <div className="relative mx-auto max-w-3xl px-5 sm:px-6 py-24 md:py-28 text-center">
          <Reveal>
            <h2 className="text-balance text-3xl md:text-5xl font-semibold tracking-tight text-fg-bright leading-[1.1]">
              {t(k("confidence.title"))}
            </h2>
          </Reveal>
          <Reveal delay={100}>
            <p className="mt-6 text-pretty text-[15px] md:text-lg leading-relaxed text-fg-dim">
              {t(k("confidence.description"))}
            </p>
          </Reveal>
          <Reveal delay={160}>
            <Link
              href="/contact"
              className="mt-8 inline-flex items-center gap-1.5 text-[14px] font-medium text-brand-300 hover:text-brand-200 transition"
            >
              {t(k("confidence.cta"))}
              <span aria-hidden>→</span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ── Company card (legal entity, deliberately understated) ─────────── */}
      <Section id="company">
        <Reveal>
          <div className="mx-auto max-w-2xl">
            <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-fg-muted text-center">
              {t(k("company.eyebrow"))}
            </div>
            <div className="mt-6 rounded-2xl border border-line-subtle bg-bg-raised p-6 text-center">
              <div className="text-[15px] font-semibold text-fg-bright">
                {t(k("company.entity"))}
              </div>
              <div className="text-[13px] text-fg-muted">
                {t(k("company.location"))}
              </div>
              <p className="mt-3 text-[14px] text-fg-dim leading-relaxed">
                {t(k("company.note"))}
              </p>
              {/* Company positioning: focused product + direct access to the
                  team that builds it — no traction a buyer can't verify, no
                  weakness framing, and no pre-emptive commercial limits. */}
              <p className="mt-3 text-[14px] text-fg-dim leading-relaxed">
                {t(k("company.stage"))}
              </p>
              <div className="mt-5 border-t border-line-subtle pt-4">
                <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-fg-muted">
                  {t(k("company.contactLead"))}
                </div>
                <a
                  href={`mailto:${PUBLIC_EMAIL}`}
                  className="mt-1 inline-block text-[14px] text-brand-300 hover:text-brand-200 transition"
                >
                  {PUBLIC_EMAIL}
                </a>
                <p className="mt-2 text-[13px] text-fg-muted leading-relaxed">
                  {t(k("company.contactNote"))}
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </Section>
    </MarketingShell>
  );
}
